import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { AdminLayout } from '../../components/admin/AdminLayout';
import { ExcelUploadModal } from '../../components/admin/ExcelUploadModal';
import { API_BASE } from '../../config/api';
import { PageShell } from '../../components/admin/PageShell';
import { DataTable } from '../../components/admin/DataTable';
import { SearchBar } from '../../components/admin/SearchBar';
import { QuickActions } from '../../components/admin/QuickActions';
import { Badge } from '../../components/admin/Badge';
import { EmptyState } from '../../components/admin/EmptyState';
import { Skeleton, SkeletonCard } from '../../components/admin/Skeleton';
import { StatCard } from '../../components/admin/StatCard';

export const UniversityList = () => {
  const navigate = useNavigate();
  const [universities, setUniversities] = useState([]);
  const [filteredUniversities, setFilteredUniversities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterCountry, setFilterCountry] = useState('all');
  const [filterPartner, setFilterPartner] = useState('all');
  const [filterPublished, setFilterPublished] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [showUploadModal, setShowUploadModal] = useState(false);

  const backendUrl = API_BASE;

  useEffect(() => {
    fetchUniversities();
  }, []);

  useEffect(() => {
    applyFilters();
  }, [universities, filterCountry, filterPartner, filterPublished, searchQuery]);

  const fetchUniversities = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('admin_token');
      
      if (!token) {
        navigate('/admin/login');
        return;
      }

      const response = await fetch(`${backendUrl}/api/admin/universities`, {
        headers: { 'Authorization': `Bearer ${token}` },
        cache: 'no-store' // Prevent caching
      });

      if (response.status === 401) {
        localStorage.removeItem('admin_token');
        navigate('/admin/login');
        return;
      }

      if (!response.ok) throw new Error('Failed to fetch universities');

      const data = await response.json();
      // Always set to an array, even if empty or undefined
      const universitiesList = Array.isArray(data.universities) ? data.universities : [];
      setUniversities(universitiesList);
    } catch (err) {
      console.error('Error fetching universities:', err);
      // Reset to empty array on error to ensure accurate stats
      setUniversities([]);
    } finally {
      setLoading(false);
    }
  };

  const applyFilters = () => {
    let filtered = [...universities];

    // Search filter
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(uni => 
        uni.name?.toLowerCase().includes(query) ||
        uni.country?.toLowerCase().includes(query) ||
        uni.partner_status?.toLowerCase().includes(query)
      );
    }

    if (filterCountry !== 'all') {
      filtered = filtered.filter(uni => uni.country === filterCountry);
    }

    if (filterPartner !== 'all') {
      filtered = filtered.filter(uni => uni.partner_status === filterPartner);
    }

    if (filterPublished !== 'all') {
      const isPublished = filterPublished === 'published';
      filtered = filtered.filter(uni => uni.published === isPublished);
    }

    // Sort by created_at, handling undefined values
    filtered.sort((a, b) => {
      const dateA = a.created_at ? new Date(a.created_at).getTime() : 0;
      const dateB = b.created_at ? new Date(b.created_at).getTime() : 0;
      return dateB - dateA; // Descending order (newest first)
    });
    setFilteredUniversities(filtered);
  };

  const handleBulkAction = async (action, selectedIds) => {
    if (selectedIds.length === 0) return;

    try {
      const token = localStorage.getItem('admin_token');
      if (!token) {
        alert('Authentication required');
        navigate('/admin/login');
        return;
      }

      if (action === 'delete') {
        if (!window.confirm(`Are you sure you want to delete ${selectedIds.length} universit${selectedIds.length > 1 ? 'ies' : 'y'}?`)) {
          return;
        }

        // Delete in parallel
        const deletePromises = selectedIds.map(id =>
          fetch(`${backendUrl}/api/admin/universities/${id}`, {
            method: 'DELETE',
            headers: { 'Authorization': `Bearer ${token}` }
          })
        );

        await Promise.all(deletePromises);
        fetchUniversities();
      } else if (action === 'publish') {
        const updatePromises = selectedIds.map(id =>
          fetch(`${backendUrl}/api/admin/universities/${id}`, {
            method: 'PUT',
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json'
            },
            body: JSON.stringify({ published: true })
          })
        );

        await Promise.all(updatePromises);
        fetchUniversities();
      } else if (action === 'unpublish') {
        const updatePromises = selectedIds.map(id =>
          fetch(`${backendUrl}/api/admin/universities/${id}`, {
            method: 'PUT',
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json'
            },
            body: JSON.stringify({ published: false })
          })
        );

        await Promise.all(updatePromises);
        fetchUniversities();
      }
    } catch (err) {
      console.error('Bulk action error:', err);
      alert('Failed to perform bulk action');
    }
  };

  const exportToCSV = () => {
    const headers = ['Name', 'Country', 'Partner Status', 'Status', 'Created At'];
    const rows = filteredUniversities.map(uni => [
      uni.name || '',
      uni.country || '',
      uni.partner_status || '',
      uni.published ? 'Published' : 'Draft',
      uni.created_at ? new Date(uni.created_at).toLocaleDateString() : ''
    ]);

    const csvContent = [
      headers.join(','),
      ...rows.map(row => row.map(cell => `"${cell}"`).join(','))
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', `universities_export_${new Date().toISOString().split('T')[0]}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  // Calculate statistics - use useMemo to ensure recalculation when universities change
  const stats = useMemo(() => {
    // Ensure we're working with the latest universities array
    const currentUniversities = Array.isArray(universities) ? universities : [];
    
    const stats = {
      total: currentUniversities.length,
      byType: {},
      byPartnerStatus: {},
      byCountry: {},
      published: 0,
      draft: 0
    };

    currentUniversities.forEach(uni => {
      // Skip if university is null/undefined
      if (!uni) return;

      // Count by institution type - handle both slug format and label format
      let instType = uni.institution_type || '';
      // Normalize: if it contains spaces or is a label, try to match to slug
      if (instType && !instType.includes('-')) {
        // Might be a label, try to convert
        const labelToSlug = {
          'Public University': 'public-university',
          'Private University': 'private-university',
          'University College': 'university-college',
          'College': 'college',
          'Polytechnic': 'polytechnic',
          'Community College': 'community-college',
          'International Branch Campus': 'international-branch'
        };
        instType = labelToSlug[instType] || instType.toLowerCase().replace(/\s+/g, '-');
      }
      if (instType) {
        stats.byType[instType] = (stats.byType[instType] || 0) + 1;
      }

      // Count by partner status - exact match
      const partnerStatus = uni.partner_status || 'Unknown';
      stats.byPartnerStatus[partnerStatus] = (stats.byPartnerStatus[partnerStatus] || 0) + 1;

      // Count by country - handle both slug and label
      let country = uni.country || '';
      // If country is a slug (contains hyphen or lowercase), use as-is
      // If it's a label (like "Malaysia"), convert to slug for consistency
      if (country && !country.includes('-') && country[0] === country[0].toUpperCase()) {
        // Likely a label, convert to slug
        country = country.toLowerCase().replace(/\s+/g, '-');
      }
      if (country) {
        stats.byCountry[country] = (stats.byCountry[country] || 0) + 1;
      }

      // Count published/draft
      if (uni.published) {
        stats.published++;
      } else {
        stats.draft++;
      }
    });

    return stats;
  }, [universities]); // Recalculate whenever universities array changes

  // Prepare stats for display
  const getInstitutionTypeLabel = (slug) => {
    const typeMap = {
      'public-university': 'Public University',
      'private-university': 'Private University',
      'university-college': 'University College',
      'college': 'College',
      'polytechnic': 'Polytechnic',
      'community-college': 'Community College',
      'international-branch': 'International Branch'
    };
    return typeMap[slug] || slug;
  };

  const getCountryLabel = (slug) => {
    // Convert slug to readable label (e.g., 'malaysia' -> 'Malaysia')
    return slug ? slug.split('-').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ') : 'Unknown';
  };

  const handleDelete = async (id) => {
    if (!id) {
      console.error('Cannot delete university: id is undefined');
      alert('Cannot delete: University ID is missing');
      return;
    }
    
    if (!window.confirm('Are you sure you want to delete this university?')) return;

    try {
      const token = localStorage.getItem('admin_token');
      if (!token) {
        alert('Authentication required. Please log in again.');
        navigate('/admin/login');
        return;
      }
      
      const response = await fetch(`${backendUrl}/api/admin/universities/${id}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.status === 401) {
        localStorage.removeItem('admin_token');
        navigate('/admin/login');
        return;
      }

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || 'Failed to delete university');
      }

      setUniversities(prev => prev.filter(uni => uni.id !== id));
      alert('University deleted successfully');
    } catch (err) {
      console.error('Delete error:', err);
      alert(`Failed to delete university: ${err.message}`);
    }
  };

  const togglePublish = async (id, currentStatus) => {
    if (!id) {
      console.error('Cannot update university: id is undefined');
      alert('Cannot update: University ID is missing');
      return;
    }
    
    try {
      const token = localStorage.getItem('admin_token');
      if (!token) {
        alert('Authentication required. Please log in again.');
        navigate('/admin/login');
        return;
      }
      
      const response = await fetch(`${backendUrl}/api/admin/universities/${id}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ published: !currentStatus })
      });

      if (response.status === 401) {
        localStorage.removeItem('admin_token');
        navigate('/admin/login');
        return;
      }

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || 'Failed to update publish status');
      }

      setUniversities(prev => 
        prev.map(uni => uni.id === id ? { ...uni, published: !currentStatus } : uni)
      );
    } catch (err) {
      console.error('Toggle publish error:', err);
      alert(`Failed to update publish status: ${err.message}`);
    }
  };

  if (loading) {
    return (
      <AdminLayout>
        <PageShell title="Universities" subtitle="Loading...">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <SkeletonCard />
            <SkeletonCard />
            <SkeletonCard />
          </div>
        </PageShell>
      </AdminLayout>
    );
  }

  const tableColumns = [
    {
      key: 'name',
      label: 'Name',
      sortable: true,
      render: (value, row) => (
        <div>
          <div className="font-semibold text-gray-900">{value || '-'}</div>
          {row.created_at && (
            <div className="text-xs text-gray-500 mt-0.5">
              Created {new Date(row.created_at).toLocaleDateString()}
            </div>
          )}
        </div>
      )
    },
    {
      key: 'country',
      label: 'Country',
      sortable: true
    },
    {
      key: 'partner_status',
      label: 'Partner',
      sortable: true,
      render: (value) => value || '-'
    },
    {
      key: 'published',
      label: 'Status',
      render: (value, row) => (
        <Badge variant={value ? 'success' : 'default'}>
          {value ? 'Published' : 'Draft'}
        </Badge>
      )
    }
  ];

  const rowActions = [
    {
      label: 'Edit',
      icon: (
        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
        </svg>
      ),
      onClick: (row) => {
        if (row.id) {
          navigate(`/admin/universities/${row.id}/edit`);
        }
      },
      variant: 'primary'
    },
    {
      label: 'Delete',
      icon: (
        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
        </svg>
      ),
      onClick: (row) => handleDelete(row.id),
      variant: 'danger'
    }
  ];

  const bulkActions = [
    {
      key: 'publish',
      label: 'Publish Selected',
      variant: 'default'
    },
    {
      key: 'unpublish',
      label: 'Unpublish Selected',
      variant: 'default'
    },
    {
      key: 'delete',
      label: 'Delete Selected',
      variant: 'danger'
    }
  ];

  const quickActions = [
    {
      label: 'Add University',
      icon: (
        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
        </svg>
      ),
      onClick: () => navigate('/admin/universities/new'),
      variant: 'primary'
    },
    {
      label: 'Bulk Import',
      icon: (
        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
        </svg>
      ),
      onClick: () => setShowUploadModal(true),
      variant: 'default'
    },
    {
      label: 'Export CSV',
      icon: (
        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
        </svg>
      ),
      onClick: exportToCSV,
      variant: 'default'
    }
  ];

  return (
    <AdminLayout>
      <PageShell
        title="Universities"
        subtitle={`${filteredUniversities.length} of ${universities.length} universities`}
        actions={
          <>
            <button
              onClick={() => setShowUploadModal(true)}
              className="px-4 py-2 bg-white border border-gray-300 text-gray-700 hover:bg-gray-50 font-semibold rounded-lg transition-colors flex items-center gap-2"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
              </svg>
              Bulk Import
            </button>
            <button
              onClick={() => navigate('/admin/universities/new')}
              className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white font-semibold rounded-lg transition-colors flex items-center gap-2"
              data-testid="add-university-button"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
              </svg>
              Add University
            </button>
          </>
        }
      >
        {/* Statistics Dashboard */}
        <div className="mb-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">University Statistics</h3>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
            <StatCard
              label="Total Universities"
              value={stats.total}
              iconBg="bg-blue-100"
              iconColor="text-blue-600"
              icon={
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                </svg>
              }
              size="compact"
            />
            <StatCard
              label="Private Universities"
              value={stats.byType['private-university'] || 0}
              iconBg="bg-purple-100"
              iconColor="text-purple-600"
              icon={
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                </svg>
              }
              size="compact"
            />
            <StatCard
              label="Public Universities"
              value={stats.byType['public-university'] || 0}
              iconBg="bg-emerald-100"
              iconColor="text-emerald-600"
              icon={
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                </svg>
              }
              size="compact"
            />
            <StatCard
              label="International Branch"
              value={stats.byType['international-branch'] || 0}
              iconBg="bg-cyan-100"
              iconColor="text-cyan-600"
              icon={
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3.055 11H5a2 2 0 012 2v1a2 2 0 002 2 2 2 0 012 2v2.945M8 3.935V5.5A2.5 2.5 0 0010.5 8h.5a2 2 0 012 2 2 2 0 104 0 2 2 0 012-2h1.064M15 20.488V18a2 2 0 012-2h3.064M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              }
              size="compact"
            />
            <StatCard
              label="Partner Universities"
              value={(stats.byPartnerStatus['Close'] || 0) + (stats.byPartnerStatus['Normal'] || 0)}
              iconBg="bg-indigo-100"
              iconColor="text-indigo-600"
              icon={
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                </svg>
              }
              size="compact"
            />
            <StatCard
              label="Non-Partner"
              value={stats.byPartnerStatus['Not Partner'] || 0}
              iconBg="bg-orange-100"
              iconColor="text-orange-600"
              icon={
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18.364 18.364A9 9 0 005.636 5.636m12.728 12.728A9 9 0 015.636 5.636m12.728 12.728L5.636 5.636" />
                </svg>
              }
              size="compact"
            />
          </div>
        </div>

        {/* Top Countries */}
        {Object.entries(stats.byCountry).length > 0 && (
          <div className="mb-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Top Countries</h3>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
              {Object.entries(stats.byCountry)
                .sort((a, b) => b[1] - a[1])
                .slice(0, 5)
                .map(([country, count], idx) => {
                  const countryLabel = getCountryLabel(country);
                  const colors = [
                    { bg: 'bg-teal-100', text: 'text-teal-600' },
                    { bg: 'bg-cyan-100', text: 'text-cyan-600' },
                    { bg: 'bg-blue-100', text: 'text-blue-600' },
                    { bg: 'bg-indigo-100', text: 'text-indigo-600' },
                    { bg: 'bg-purple-100', text: 'text-purple-600' }
                  ];
                  const color = colors[idx % colors.length];
                  return (
                    <StatCard
                      key={`country-${country}`}
                      label={countryLabel}
                      value={count}
                      iconBg={color.bg}
                      iconColor={color.text}
                      icon={
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3.055 11H5a2 2 0 012 2v1a2 2 0 002 2 2 2 0 012 2v2.945M8 3.935V5.5A2.5 2.5 0 0010.5 8h.5a2 2 0 012 2 2 2 0 104 0 2 2 0 012-2h1.064M15 20.488V18a2 2 0 012-2h3.064M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                      }
                      size="compact"
                    />
                  );
                })}
            </div>
          </div>
        )}

        {/* Quick Actions */}
        <QuickActions actions={quickActions} />

        {/* Search and Filters */}
        <div className="bg-white rounded-xl border border-gray-200 p-6">
          <SearchBar
            value={searchQuery}
            onChange={setSearchQuery}
            placeholder="Search universities by name, country, or partner status..."
            filters={[
              {
                key: 'country',
                value: filterCountry,
                options: [
                  { value: 'all', label: 'All Countries' },
                  { value: 'Malaysia', label: 'Malaysia' },
                  { value: 'USA', label: 'USA' },
                  { value: 'UK', label: 'UK' },
                  { value: 'Australia', label: 'Australia' }
                ]
              },
              {
                key: 'partner',
                value: filterPartner,
                options: [
                  { value: 'all', label: 'All Partners' },
                  { value: 'Close', label: 'Close Partner' },
                  { value: 'Normal', label: 'Normal Partner' },
                  { value: 'Not Partner', label: 'Not Partner' }
                ]
              },
              {
                key: 'published',
                value: filterPublished,
                options: [
                  { value: 'all', label: 'All Status' },
                  { value: 'published', label: 'Published' },
                  { value: 'draft', label: 'Draft' }
                ]
              }
            ]}
            onFilterChange={(key, value) => {
              if (key === 'country') setFilterCountry(value);
              else if (key === 'partner') setFilterPartner(value);
              else if (key === 'published') setFilterPublished(value);
            }}
          />
        </div>

        {/* Data Table */}
        <DataTable
          columns={tableColumns}
          data={filteredUniversities.filter(uni => uni.id)}
          loading={loading}
          onBulkAction={handleBulkAction}
          bulkActions={bulkActions}
          rowActions={rowActions}
          emptyState={
            <EmptyState
              icon={
                <svg className="w-8 h-8 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                </svg>
              }
              title="No universities found"
              description={searchQuery || filterCountry !== 'all' || filterPartner !== 'all' || filterPublished !== 'all' 
                ? "Try adjusting your search or filters."
                : "Get started by adding your first university."}
              action={
                <button
                  onClick={() => navigate('/admin/universities/new')}
                  className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white font-semibold rounded-lg transition-colors"
                >
                  Add University
                </button>
              }
            />
          }
        />
      </PageShell>

      {/* Excel Upload Modal */}
      <ExcelUploadModal
        entity="universities"
        entityLabel="Universities"
        isOpen={showUploadModal}
        onClose={() => setShowUploadModal(false)}
        onSuccess={() => {
          fetchUniversities();
          setShowUploadModal(false);
        }}
      />
    </AdminLayout>
  );
};

export default UniversityList;
